import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { imgUrl, heroImgUrl, catImgUrl } from '../utils';
import './Home.css';

export default function Home() {
  const [settings, setSettings] = useState({});
  const [featured, setFeatured] = useState([]);
  const [heroProducts, setHeroProducts] = useState([null, null, null]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    api.get('/products?featured=true').then(r => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  // Once settings are loaded, fetch the admin-selected hero products if configured
  useEffect(() => {
    if (!settings || Object.keys(settings).length === 0) return;
    const ids = [settings.hero_product_1, settings.hero_product_2, settings.hero_product_3];
    const hasAnyProductId = ids.some(Boolean);
    if (!hasAnyProductId) return;
    Promise.all(
      ids.map(id => id ? api.get(`/products/${id}`).then(r => r.data).catch(() => null) : Promise.resolve(null))
    ).then(setHeroProducts);
  }, [settings]);

  // Determine what to show in each hero slot:
  // 1. Admin-uploaded hero image (hero_upload_image_N)
  // 2. Admin-selected product's primary image (heroProducts[N])
  // 3. Fall back to featured[N] if nothing configured
  const getHeroSlot = (i) => {
    const uploadKey = `hero_upload_image_${i + 1}`;
    if (settings[uploadKey]) {
      return { type: 'upload', src: heroImgUrl(settings[uploadKey]), id: null };
    }
    const prod = heroProducts[i] || featured[i];
    if (prod?.primary_image) {
      return { type: 'product', src: imgUrl(prod.primary_image), id: prod._id };
    }
    return null;
  };

  // Get the top-level category object by slug
  const getCatBySlug = (slug) => categories.find(c => c.slug === slug);

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero-grid">
          <div className="hero-content">
            <p className="tag hero-tag">{settings.hero_tag || 'Wholesale Collection 2026'}</p>
            <h1 className="hero-title">
              {settings.hero_title || 'Direct from Factory to Your Store'}
            </h1>
            <div className="divider" />
            <p className="hero-desc">
              {settings.hero_subtitle || 'Premium wholesale knitwear and fashion. Women, Men & Children collections crafted at source.'}
            </p>
            <div className="hero-actions">
              <Link to="/catalogue" className="btn-primary">Browse Catalogue</Link>
              <Link to="/register" className="btn-secondary">Register as Buyer</Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-images">
              {[0,1,2].map(i => {
                const slot = getHeroSlot(i);
                return slot ? (
                  <div
                    key={i}
                    className={`hero-img hero-img-${i+1}`}
                    onClick={() => slot.id && navigate(`/product/${slot.id}`)}
                    style={slot.id ? { cursor: 'pointer' } : {}}
                  >
                    <img src={slot.src} alt={`Hero ${i+1}`} />
                  </div>
                ) : (
                  <div key={i} className={`hero-img hero-img-${i+1} hero-img-placeholder`}>
                    <span>✦</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-bar">
        {['Women\'s Collection','◆','Men\'s Collection','◆','Children\'s Range','◆','Ponchos','◆','Cardigans','◆','Knitwear','◆','Factory Direct','◆','UK Wholesale'].map((t,i) => (
          <span key={i} className={t==='◆'?'marquee-dot':'marquee-item'}>{t}</span>
        ))}
      </div>

      {/* CATEGORIES */}
      <section className="home-categories container">
        <div className="section-header">
          <p className="tag">Shop by Collection</p>
          <h2 className="section-title">Three Collections,<br/><em>Endless Choice</em></h2>
        </div>
        <div className="cat-grid">
          {[
            { slug:'women', label:'Women', subs:'Ponchos · Cardigans · Dresses · Knitwear' },
            { slug:'men', label:'Men', subs:'T-Shirts · Jeans · Cardigans · Outerwear' },
            { slug:'children', label:'Children', subs:'T-Shirts · Knitwear · Jeans · Sets' },
          ].map(cat => {
            const catData = getCatBySlug(cat.slug);
            return (
              <Link key={cat.slug} to={`/catalogue?category=${cat.slug}`} className="cat-card">
                <div className="cat-card-img">
                  {catData?.image
                    ? <img src={catImgUrl(catData.image)} alt={cat.label} />
                    : <div className="cat-placeholder"><span>{cat.label[0]}</span></div>
                  }
                </div>
                <div className="cat-card-body">
                  <h3 className="cat-name">{cat.label}</h3>
                  <p className="cat-subs">{cat.subs}</p>
                  <span className="cat-link">View Collection →</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="home-featured">
          <div className="container">
            <div className="section-header">
              <p className="tag">Featured Products</p>
              <h2 className="section-title">New Season<br/><em>Highlights</em></h2>
            </div>
            <div className="featured-grid">
              {featured.map(p => (
                <Link key={p._id} to={`/product/${p._id}`} className="featured-card">
                  <div className="featured-img">
                    {p.primary_image
                      ? <img src={imgUrl(p.primary_image)} alt={p.name} />
                      : <div className="feat-placeholder"><span>{p.name[0]}</span></div>
                    }
                    <div className="featured-overlay">
                      <span>View Product</span>
                    </div>
                  </div>
                  <div className="featured-info">
                    <p className="featured-cat">{p.parent_category_name} · {p.category_name}</p>
                    <h4 className="featured-name">{p.name}</h4>
                    <p className="featured-moq">Min. order: {p.min_order_qty} units</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="featured-cta">
              <Link to="/catalogue" className="btn-secondary">View Full Catalogue</Link>
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="why-us container">
        <div className="why-grid">
          {[
            { icon:'🏭', title:'Factory Direct', desc:'No middlemen. Our products come straight from production, giving you better prices and consistent quality.' },
            { icon:'👁', title:'Exclusive Pricing', desc:'Create an authorised buyer account to unlock wholesale pricing and place orders directly through the platform.' },
            { icon:'📦', title:'Simple Ordering', desc:'Build your basket, choose your quantities, submit your order. We confirm and invoice — you pay by bank transfer.' },
            { icon:'🇬🇧', title:'UK Wholesale', desc:'Serving UK retailers and distributors. Bank transfer payments. Proforma invoice on every order.' },
          ].map((w,i) => (
            <div key={i} className="why-card">
              <div className="why-icon">{w.icon}</div>
              <h4 className="why-title">{w.title}</h4>
              <p className="why-desc">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REGISTER CTA */}
      <section className="register-cta">
        <div className="container">
          <div className="register-cta-inner">
            <div>
              <p className="tag">Wholesale Access</p>
              <h2 className="register-cta-title">Become an<br/><em>Authorised Buyer</em></h2>
              <p className="register-cta-desc">Register your business to unlock wholesale pricing, minimum order details, and place orders directly. VAT number optional.</p>
              <Link to="/register" className="btn-terracotta" style={{marginTop:'32px'}}>Register Your Business</Link>
            </div>
            <div className="register-cta-steps">
              {['Fill in your company details','Submit registration request','Admin reviews and approves','Access prices and place orders'].map((s,i) => (
                <div key={i} className="step">
                  <div className="step-num">{String(i+1).padStart(2,'0')}</div>
                  <div className="step-text">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
